import { clearUserContentHistory } from '../../lib/api.ts';
import { getIconTemplate, ICON_MAGNIFYING_GLASS } from '../../../shared/lib/icons.ts';
import { logException } from '../../../shared/lib/logging.ts';

const CLASS_LIST_DROPDOWN_HEADER = ['dropdown-header', 'd-flex', 'align-items-center', 'justify-content-between', 'h6'];
const CLASS_LIST_DROPDOWN_ITEM_TEXT = ['dropdown-item-text', 'py-2', 'text-break'];
const CLASS_LIST_DROPDOWN_ITEM = ['dropdown-item', 'd-flex', 'align-items-center', 'py-2'];
const CLASS_LIST_SEARCH_ITEM_IMAGE = ['flex-shrink-0', 'object-fit-contain', 'me-3'];
const CLASS_LIST_SEARCH_ITEM_IMAGE_PLACEHOLDER = ['flex-shrink-0', 'bg-light', 'me-3'];
const CLASS_LIST_SEARCH_ITEM_TEXT = ['text-truncate'];
const CLASS_LIST_SEARCH_ITEM_MARK = ['p-0'];
const CLASS_LIST_SEARCH_HISTORY_ITEM_ICON = [
	'flex-shrink-0',
	'd-flex',
	'align-items-center',
	'justify-content-center',
	'me-3',
];
const CLASS_LIST_SEARCH_HISTORY_CLEAR_BUTTON = ['btn-close', 'w-2', 'h-2', 'p-0'];

const SEARCH_ITEM_IMAGE_WIDTH = 32;
const SEARCH_ITEM_IMAGE_HEIGHT = 32;

const SEARCH_ITEM_HISTORY_ICON_WRAPPER_WIDTH = 32;
const SEARCH_ITEM_HISTORY_ICON_WRAPPER_HEIGHT = 16;

const imageMap = new Map();

export const createMenuRenderer = ($menu: HTMLUListElement) => {
	const renderSearchFor = (searchForText: string, searchText: string) => {
		const $item = document.createElement('li');

		const $text = document.createElement('div');
		$text.classList.add(...CLASS_LIST_SEARCH_ITEM_TEXT);
		$text.innerHTML = `${searchForText} <mark class="${CLASS_LIST_SEARCH_ITEM_MARK}">${searchText}</mark>...`;

		const $button = document.createElement('button');
		$button.classList.add(...CLASS_LIST_DROPDOWN_ITEM);
		$button.type = 'submit';

		$button.appendChild($text);

		$item.appendChild($button);

		$menu.appendChild($item);

		return $item;
	};

	const renderItemsHeader = (headerText: string) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $header = document.createElement('div');
		$header.classList.add(...CLASS_LIST_DROPDOWN_HEADER);
		$header.innerText = headerText;

		$item.appendChild($header);

		return $item;
	};

	const renderItem = (searchTextRegex: RegExp, name: string, url: string, imageUrl: string | null) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $link = document.createElement('a');
		$link.classList.add(...CLASS_LIST_DROPDOWN_ITEM);
		$link.href = url;

		$item.appendChild($link);

		const $text = document.createElement('div');
		$text.classList.add(...CLASS_LIST_SEARCH_ITEM_TEXT);
		$text.innerHTML = name.replace(searchTextRegex, `<mark class="${CLASS_LIST_SEARCH_ITEM_MARK}">$&</mark>`);

		$link.appendChild($text);

		if (imageUrl) {
			let $image: HTMLImageElement;

			if (!imageMap.has(imageUrl)) {
				$image = document.createElement('img');
				$image.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE);
				$image.src = imageUrl;
				$image.alt = name;
				$image.width = SEARCH_ITEM_IMAGE_WIDTH;
				$image.height = SEARCH_ITEM_IMAGE_HEIGHT;

				$image.addEventListener('error', () => {
					const $imagePlaceholder = document.createElement('div');
					$imagePlaceholder.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE_PLACEHOLDER);
					$imagePlaceholder.style.width = `${SEARCH_ITEM_IMAGE_WIDTH}px`;
					$imagePlaceholder.style.height = `${SEARCH_ITEM_IMAGE_HEIGHT}px`;

					$image.replaceWith($imagePlaceholder);

					imageMap.delete(imageUrl);
				});

				imageMap.set(imageUrl, $image);
			} else {
				$image = imageMap.get(imageUrl);
			}

			$link.prepend($image);
		} else {
			const $imagePlaceholder = document.createElement('div');
			$imagePlaceholder.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE_PLACEHOLDER);
			$imagePlaceholder.style.width = `${SEARCH_ITEM_IMAGE_WIDTH}px`;
			$imagePlaceholder.style.height = `${SEARCH_ITEM_IMAGE_HEIGHT}px`;

			$link.prepend($imagePlaceholder);
		}

		return $item;
	};

	const renderNoResults = (noResultsText: string, searchText: string) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $text = document.createElement('div');
		$text.classList.add(...CLASS_LIST_DROPDOWN_ITEM_TEXT);
		$text.innerHTML = `${noResultsText} <mark class="${CLASS_LIST_SEARCH_ITEM_MARK}">${searchText}</mark>`;

		$item.appendChild($text);

		return $item;
	};

	const renderHistoryItemsHeader = (headerText: string, cleanupCallback: () => void) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $header = document.createElement('div');
		$header.classList.add(...CLASS_LIST_DROPDOWN_HEADER);
		$header.innerText = headerText;

		$item.appendChild($header);

		const $clearButton = document.createElement('button');
		$clearButton.classList.add(...CLASS_LIST_SEARCH_HISTORY_CLEAR_BUTTON);
		$clearButton.setAttribute('type', 'button');
		$clearButton.addEventListener('click', () => {
			cleanupCallback();
		});

		$header.appendChild($clearButton);

		return $item;
	};

	const renderContentItemsHeader = (headerText: string, cleanupCallback: () => void) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $header = document.createElement('div');
		$header.classList.add(...CLASS_LIST_DROPDOWN_HEADER);
		$header.innerText = headerText;

		$item.appendChild($header);

		const $clearButton = document.createElement('button');
		$clearButton.classList.add(...CLASS_LIST_SEARCH_HISTORY_CLEAR_BUTTON);
		$clearButton.setAttribute('type', 'button');
		$clearButton.addEventListener('click', () => {
			clearUserContentHistory().catch((e) => {
				logException(e);
			});

			cleanupCallback();
		});

		$header.appendChild($clearButton);

		return $item;
	};

	const renderHistorySearchItem = (name: string, formAction: string) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $link = document.createElement('a');
		$link.classList.add(...CLASS_LIST_DROPDOWN_ITEM);
		$link.href = `${formAction}?search=${name}`;

		$item.appendChild($link);

		const $text = document.createElement('div');
		$text.classList.add(...CLASS_LIST_SEARCH_ITEM_TEXT);
		$text.innerHTML = name;

		$link.appendChild($text);

		const $iconWrapper = document.createElement('span');
		$iconWrapper.classList.add(...CLASS_LIST_SEARCH_HISTORY_ITEM_ICON);
		$iconWrapper.style.width = `${SEARCH_ITEM_HISTORY_ICON_WRAPPER_WIDTH}px`;
		$iconWrapper.style.height = `${SEARCH_ITEM_HISTORY_ICON_WRAPPER_HEIGHT}px`;

		$link.prepend($iconWrapper);

		const $icon = document.createElement('template');
		$icon.innerHTML = getIconTemplate(ICON_MAGNIFYING_GLASS)('', 'width: 14px; height: 14px;');

		$iconWrapper.prepend($icon.content.firstChild as ChildNode);

		return $item;
	};

	const renderHistoryContentItem = (name: string, url: string, imageUrl: string | null) => {
		const $item = document.createElement('li');

		$menu.appendChild($item);

		const $link = document.createElement('a');
		$link.classList.add(...CLASS_LIST_DROPDOWN_ITEM);
		$link.href = url;

		$item.appendChild($link);

		const $text = document.createElement('div');
		$text.classList.add(...CLASS_LIST_SEARCH_ITEM_TEXT);
		$text.innerHTML = name;

		$link.appendChild($text);

		if (imageUrl) {
			let $image: HTMLImageElement;

			if (!imageMap.has(imageUrl)) {
				$image = document.createElement('img');
				$image.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE);
				$image.src = imageUrl;
				$image.alt = name;
				$image.width = SEARCH_ITEM_IMAGE_WIDTH;
				$image.height = SEARCH_ITEM_IMAGE_HEIGHT;

				$image.addEventListener('error', () => {
					const $imagePlaceholder = document.createElement('div');
					$imagePlaceholder.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE_PLACEHOLDER);
					$imagePlaceholder.style.width = `${SEARCH_ITEM_IMAGE_WIDTH}px`;
					$imagePlaceholder.style.height = `${SEARCH_ITEM_IMAGE_HEIGHT}px`;

					$image.replaceWith($imagePlaceholder);

					imageMap.delete(imageUrl);
				});

				imageMap.set(imageUrl, $image);
			} else {
				$image = imageMap.get(imageUrl);
			}

			$link.prepend($image);
		} else {
			const $imagePlaceholder = document.createElement('div');
			$imagePlaceholder.classList.add(...CLASS_LIST_SEARCH_ITEM_IMAGE_PLACEHOLDER);
			$imagePlaceholder.style.width = `${SEARCH_ITEM_IMAGE_WIDTH}px`;
			$imagePlaceholder.style.height = `${SEARCH_ITEM_IMAGE_HEIGHT}px`;

			$link.prepend($imagePlaceholder);
		}

		return $item;
	};

	const removeListItem = ($item: HTMLLIElement) => {
		$menu.removeChild($item);

		return null;
	};

	const removeListItems = ($items: (HTMLLIElement | null)[]) => {
		$items.forEach(($item) => {
			if ($item) removeListItem($item);
		});

		return [];
	};

	return {
		renderSearchFor,
		renderItemsHeader,
		renderItem,
		renderNoResults,
		renderHistoryItemsHeader,
		renderContentItemsHeader,
		renderHistorySearchItem,
		renderHistoryContentItem,
		removeListItem,
		removeListItems,
	};
};
